/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SwanGetFitted from "./swanGetFitted/SwanGetFitted";
import { AttributesContext } from "../context/AttributesContext";
import { getEndpointFitViewCustomScan, getEndpointFitViewScan, getEndpointProductSupportBySwan, setLocalStorage, setupCTADisplay, storeSubscriptionCheck } from "../utils/Utils";
import { MESSAGE_KEY, REACT_APP_CHILD_URL, STORAGE_KEY } from "../services/constant";
import { posthog } from "posthog-js";
import useGetParams from "../hooks/useGetParams";
import Modal from "./modal/Modal";
import UseApiService from "../services/apiService";

const ParentApp = () => {
	const { attributesData } = useContext(AttributesContext);
	const { shopDomain, product, name, callbackUrl } = attributesData ?? {};
	const [details, setDetails] = useState();
	const [loading, setLoading] = useState(true);
	const [showButton, setShowButton] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [innerText, setInnerText] = useState();
	const [redirectLink, setRedirectLink] = useState();
	const isCustomRef = useRef(false);
	const iframeRef = useRef();
	useGetParams(attributesData, setDetails);

	const setupSwanCTA = useCallback(
		async (custom) => {
			const { email, shopifyRedirectUrl, scanID, token } = details ?? {};
			const commonSetup = async (additionalParams = {}) => {
				await setupCTADisplay({
					productHandle: product?.handle,
					shopDomain,
					token,
					isCustom: custom || isCustomRef.current,
					shopifyRedirectUrl,
					scanID,
					email,
					name,
					callbackUrl,
					setUpcallBack: setupSwanCTA,
					handleInnerText,
					...additionalParams,
				});
			};
			if (custom || isCustomRef.current) {
				await commonSetup();
				return;
			}
			const endpoint = getEndpointProductSupportBySwan({ productHandle: product?.handle, shopDomain });
			try {
				const response = await UseApiService.get({ url: endpoint });
				const { isOnboadedRTW, isOnboadedTryon } = response?.data;
				posthog.capture(`${shopDomain}/product_request`, {
					productHandle: product?.handle,
					isOnboadedRTW,
					isOnboadedTryon,
				});
				if (isOnboadedRTW || isOnboadedTryon) {
					await commonSetup({ isOnboadedTryon, isOnboadedRTW });
				} else {
					hideSwanButton("Product is not onboarded with Swan");
				}
			} catch (error) {
				hideSwanButton(error);
			}
		},
		[details, shopDomain, product, isCustomRef.current, name, shopDomain, callbackUrl],
	);

	const hideIframe = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const messageHandler = useCallback(
		(event) => {
			if (event.origin !== REACT_APP_CHILD_URL) {
				return;
			}
			let userEmail, scanID;
			const { type, data } = event.data;
			if (typeof type !== "string" || (type !== MESSAGE_KEY.REQUEST && type !== MESSAGE_KEY.DETAILS)) {
				return;
			}
			if (data === MESSAGE_KEY.REDIRECT) {
				const endpointFunction = isCustomRef.current ? getEndpointFitViewCustomScan : getEndpointFitViewScan;
				const params = {
					productHandle: product?.handle,
					shopDomain,
					shopifyRedirectUrl: details?.shopifyRedirectUrl,
					callbackUrl,
					...(isCustomRef.current && { email: details?.email, name }),
				};

				const url = endpointFunction(params);
				window.location.href = url;
			} else if (data === MESSAGE_KEY.CLOSE) {
				hideIframe();
			} else if (type === MESSAGE_KEY.DETAILS) {
				const { email, scanId } = data;
				if (email) {
					userEmail = email;
					setLocalStorage(STORAGE_KEY.EMAIL, userEmail);
				}
				if (scanId && scanId !== "null") {
					scanID = scanId;
					setLocalStorage(STORAGE_KEY.SCAN_ID, scanId);
				}
				if (userEmail || scanID) {
					hideIframe();
					setupSwanCTA();
				}
				setDetails({ ...details, email: userEmail, scanID });
			}
		},
		[REACT_APP_CHILD_URL, product, details, hideIframe, setupSwanCTA, isCustomRef.current, callbackUrl],
	);

	const handleDataTransfer = useCallback(() => {
		if (!iframeRef.current || !iframeRef.current.contentWindow) {
			console.error("Iframe is not available or not loaded yet.");
			return;
		}
		const data = {
			type: MESSAGE_KEY.INFO,
			data: {
				shopDomain,
				productHandle: product?.handle,
				shopify_redirect_url: details?.shopifyRedirectUrl,
				email: details?.email,
				isCustom: isCustomRef.current,
				name,
				callbackUrl
			},
		};
		setTimeout(() => {
			iframeRef.current.contentWindow.postMessage(data, REACT_APP_CHILD_URL);
		}, 100);
	}, [shopDomain, product, details, isCustomRef.current,name,callbackUrl]);

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleRedirect = useCallback(() => {
		if (redirectLink) {
			window.location.href = redirectLink;
		} else {
			handleOpenModal();
		}
	}, [redirectLink]);

	const handleInnerText = useCallback(
		(text, defaultValue, link) => {
			setLoading(false);
			setInnerText(text);
			if (defaultValue) {
				window.addEventListener("message", messageHandler);
			} else {
				setRedirectLink(link);
			}
		},
		[messageHandler, isCustomRef.current],
	);

	const hideSwanButton = useCallback(
		(reason) => {
			console.log(reason, "error reason");
			setShowButton(false);
		},
		[showButton],
	);
	const handleShowSwanCTA = useCallback(async () => {
		try {
			const custom = await storeSubscriptionCheck(shopDomain);
			isCustomRef.current = custom;
			setupSwanCTA(custom);
		} catch (error) {
			hideSwanButton(error);
		}
	}, [shopDomain, details]);

	useEffect(() => {
		if (details) {
			handleShowSwanCTA();
		}
		return () => {
			window.removeEventListener("message", messageHandler);
		};
	}, [details]);

	useEffect(() => {
		if (isModalOpen) {
			const iframe = iframeRef.current;
			if (iframe) {
				const handleLoad = () => {
					handleDataTransfer(); // Ensure the iframe is loaded before posting a message.
				};
				iframe.addEventListener("load", handleLoad);

				return () => {
					iframe.removeEventListener("load", handleLoad);
				};
			}
		}
	}, [isModalOpen, handleDataTransfer, isCustomRef.current, name, shopDomain, details,callbackUrl]);

	return showButton ? (
		<>
			<SwanGetFitted loading={loading} data={innerText} handleRedirect={handleRedirect} />
			{isModalOpen && createPortal(<Modal ref={iframeRef} />, document.body)}
		</>
	) : (
		<></>
	);
};

export default ParentApp;
